import React from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Loader2 } from 'lucide-react';

interface ViewDialogProps {
  isDark: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  isLoading?: boolean;
  children: React.ReactNode;
}

export function ViewDialog({
  isDark,
  isOpen,
  onOpenChange,
  title,
  description,
  isLoading = false,
  children,
}: ViewDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className={cn(
        'max-w-4xl max-h-[90vh] overflow-y-auto',
        isDark ? 'bg-[#1E293B] border-slate-700' : 'bg-white'
      )}>
        <DialogHeader>
          <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#0F172A]')}>
            {title}
          </DialogTitle>
          <DialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>
            {description}
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#0EA5A4]" />
          </div>
        ) : (
          children
        )}
        <DialogFooter>
          <Button
            onClick={() => onOpenChange(false)}
            className="bg-[#0EA5A4] hover:bg-[#0EA5A4]/90"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Helper component for detail sections
interface DetailSectionProps {
  title: string;
  isDark: boolean;
  cols?: 2 | 3;
  children: React.ReactNode;
}

export function DetailSection({ title, isDark, cols = 2, children }: DetailSectionProps) {
  return (
    <div>
      <h3 className={cn(
        'text-lg font-semibold mb-4',
        isDark ? 'text-white' : 'text-[#0F172A]'
      )}>{title}</h3>
      <div className={cn(
        'grid gap-4',
        cols === 3 ? 'grid-cols-3' : 'grid-cols-2'
      )}>
        {children}
      </div>
    </div>
  );
}

// Helper component for detail items
interface DetailItemProps {
  label: string;
  isDark: boolean;
  mono?: boolean;
  breakAll?: boolean;
  children: React.ReactNode;
}

export function DetailItem({ label, isDark, mono, breakAll, children }: DetailItemProps) {
  const isTextContent = typeof children === 'string' || typeof children === 'number';
  
  return (
    <div className="space-y-1">
      <p className={cn(
        'text-xs font-medium',
        isDark ? 'text-slate-400' : 'text-slate-500'
      )}>{label}</p>
      {isTextContent ? (
        <p className={cn(
          'text-sm',
          isDark ? 'text-slate-300' : 'text-slate-700',
          mono && 'font-mono',
          breakAll && 'break-all'
        )}>{children}</p>
      ) : children}
    </div>
  );
}
